import {Box, Text} from '@mantine/core';
import type {FC} from 'react';
import {Spinner} from 'src/design/atoms/Spinner';

/**
 * Table body loading/empty-state switcher: shows a spinner while loading,
 * otherwise an empty-state message when there is no data.
 * @registry Table loading/empty-state block (spinner or "not found" text).
 *
 * Ported from `library/tables/TableBodyRender/TableBodyRender` (+
 * `components/NoDataRender`). DEVIATION: the original `NoDataRender` used
 * `@mui/material` `Box` and a baked-in `src/Assets` `FormSVG` watermark,
 * both of which are banned imports in `design/`. Rewritten with Mantine
 * `Box`/`Text` keeping the same text treatment (centered, dark-gray, default
 * "**404** Item was not found" with bold 404, overridable via `noDataText`);
 * the SVG watermark was replaced with an optional `illustration` prop so
 * callers can pass their own node instead of a hardcoded asset. The empty
 * state is kept inline in this file (not re-exported separately) since it
 * has no standalone use outside `TableBodyRender`.
 */
export interface TableBodyRenderProps {
  isLoading?: boolean;
  hasData?: boolean;
  noDataText?: string;
  illustration?: React.ReactNode;
}

export const TableBodyRender: FC<TableBodyRenderProps> = ({
  isLoading,
  hasData,
  noDataText,
  illustration,
}) => (
  <>
    {isLoading && <Spinner loadingText="Loading...." />}
    {!isLoading && !hasData && (
      <Box mt="1rem" ta="center" c="dimmed" fz="1.25rem">
        <Text component="div" fz={{base: '1rem', sm: '1.25rem'}}>
          {noDataText ?? (
            <>
              <b>404</b> Item was not found
            </>
          )}
        </Text>
        {illustration && (
          <Box mt="md" maw="20rem" mx="auto">
            {illustration}
          </Box>
        )}
      </Box>
    )}
  </>
);
